import React, { useState } from "react";
import { ArrowLeft, Calendar, Eye, EyeOff, ShieldCheck, UserPlus, X } from "lucide-react";
import { applyDisplayPreferences, applyThemeClass } from "../utils/theme";
import { generateRecoveryPhrase } from "../api/auth";
import RecoveryPhraseSetup from "./RecoveryPhraseSetup";

const isValidDob = (dob: string): boolean => {
  if (!dob) return true;
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(dob)) return false;
  const [day, month, year] = dob.split("/").map(Number);
  const date = new Date(year, month - 1, day);
  return year >= 1900
    && year <= new Date().getFullYear()
    && date.getFullYear() === year
    && date.getMonth() === month - 1
    && date.getDate() === day;
};

const isValidSimpleEmail = (email: string): boolean => (
  !email || /^[^\s@]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/i.test(email.trim())
);

type Props = {
  onClose: () => void;
  onSwitchToLogin: () => void;
};

const inputClass = `bs-auth-input w-full rounded-md border border-white/10 bg-white/[0.06] px-4 py-3 text-white
  placeholder-gray-500 transition-all duration-200 focus:border-fuchsia-300/60 focus:ring-2 focus:ring-fuchsia-300/15`;

const SignupModal: React.FC<Props> = ({ onClose, onSwitchToLogin }) => {
  const [stage, setStage] = useState<"identity" | "recovery">("identity");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [recoveryPhrase, setRecoveryPhrase] = useState("");
  const [recoveryConfirmed, setRecoveryConfirmed] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [dobError, setDobError] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [phraseLoading, setPhraseLoading] = useState(false);

  const API_BASE = (process.env.REACT_APP_API_BASE || "http://localhost:5000").replace(/\/$/, "");

  const handleDobChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value.replace(/\D/g, "").slice(0, 8);
    const formatted = digits.length <= 2
      ? digits
      : digits.length <= 4
        ? `${digits.slice(0, 2)}/${digits.slice(2)}`
        : `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    setDob(formatted);
    setDobError(Boolean(formatted.length === 10 && !isValidDob(formatted)));
    setError("");
  };

  const validateIdentity = () => {
    const cleanUsername = username.trim();
    if (!cleanUsername || !password || !confirmPassword) return "Username and both password fields are required.";
    if (!/^[a-zA-Z0-9_]{3,30}$/.test(cleanUsername)) {
      return "Username must be 3-30 characters and use only letters, numbers, or underscores.";
    }
    if (password.length < 8 || password.length > 100) return "Password must be 8-100 characters.";
    if (!/[A-Za-z]/.test(password) || !/\d/.test(password)) return "Password needs at least one letter and one number.";
    if (password !== confirmPassword) return "Passwords do not match.";
    if (!isValidSimpleEmail(email)) return "Please enter a valid email (user@domain.tld).";
    if (phone && !/^\d{1,10}$/.test(phone)) return "Phone number must be up to 10 digits.";
    if (!isValidDob(dob)) return "Please enter a valid date of birth.";
    return "";
  };

  const loadRecoveryPhrase = async () => {
    setPhraseLoading(true);
    setError("");
    setRecoveryConfirmed(false);
    try {
      setRecoveryPhrase(await generateRecoveryPhrase());
      return true;
    } catch (err: any) {
      setError(err?.message || "Could not generate a recovery phrase.");
      return false;
    } finally {
      setPhraseLoading(false);
    }
  };

  const continueToRecovery = async () => {
    const validationError = validateIdentity();
    if (validationError) {
      setError(validationError);
      setDobError(Boolean(dob && !isValidDob(dob)));
      return;
    }
    if (await loadRecoveryPhrase()) setStage("recovery");
  };

  const handleSignup = async () => {
    const validationError = validateIdentity();
    if (validationError) {
      setError(validationError);
      setStage("identity");
      return;
    }
    if (!recoveryPhrase || !recoveryConfirmed) {
      setError("Confirm the requested recovery words before creating your identity.");
      return;
    }

    setError("");
    setSuccess("");
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: username.trim(),
          password,
          recoveryPhrase,
          email: email.trim(),
          phone: phone.trim(),
          dob,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Signup failed");

      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      localStorage.setItem("userData", JSON.stringify(data.user));
      const theme = data.user?.theme || data.user?.settings?.theme || "AMOLED";
      const fontSize = data.user?.settings?.fontSize || "Medium";
      const glow = data.user?.settings?.glow !== false;
      localStorage.setItem("theme", theme);
      localStorage.setItem("fontSize", fontSize);
      localStorage.setItem("glow", glow ? "true" : "false");
      applyThemeClass(theme);
      applyDisplayPreferences(fontSize, glow);

      setRecoveryPhrase("");
      setSuccess(`Identity created for ${data.user?.username || username.trim()}.`);
      window.setTimeout(() => {
        onClose();
        window.location.assign("/home");
      }, 1200);
    } catch (err: any) {
      setError(err?.message || "Signup failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="contents">
      <div className="blackspace-auth-dialog w-full max-w-md outline-none" onClick={(event) => event.stopPropagation()}>
        <div className="blackspace-auth-card bs-auth-panel max-h-[calc(100dvh-1.5rem)] w-full overflow-y-auto p-5 text-white animate-fade-in sm:max-h-[calc(100dvh-2rem)] sm:p-7">
          <div className="bs-auth-heading mb-7 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-md border border-fuchsia-300/25 bg-fuchsia-300/10 p-2.5">
                {stage === "identity" ? <UserPlus size={23} className="text-fuchsia-200" /> : <ShieldCheck size={23} className="text-fuchsia-200" />}
              </div>
              <div>
                <h2 className="bg-gradient-to-r from-fuchsia-200 via-cyan-200 to-amber-100 bg-clip-text text-2xl font-black text-transparent">
                  {stage === "identity" ? "Create identity" : "Secure your return"}
                </h2>
                <p className="mt-1 text-xs font-medium text-gray-400">
                  {stage === "identity" ? "Choose how Blackspace knows you." : "Save the only phrase that can recover this identity."}
                </p>
              </div>
            </div>
            <button type="button" onClick={onClose} aria-label="Close signup" className="rounded-md border border-white/10 p-2 text-gray-400 transition hover:bg-white/10 hover:text-white">
              <X size={20} />
            </button>
          </div>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (stage === "identity") void continueToRecovery();
              else void handleSignup();
            }}
            className="bs-auth-form space-y-4"
          >
            {stage === "identity" ? (
              <>
                <div className="space-y-2">
                  <label htmlFor="signup-username" className="bs-auth-field-label block">Username <span className="text-red-400">*</span></label>
                  <input id="signup-username" value={username} maxLength={30} autoComplete="username" onChange={(event) => { setUsername(event.target.value); setError(""); }} placeholder="3-30 letters, numbers, or underscores" className={inputClass} />
                </div>

                <div className="space-y-2">
                  <label htmlFor="signup-password" className="bs-auth-field-label block">Password <span className="text-red-400">*</span></label>
                  <div className="relative">
                    <input id="signup-password" type={showPassword ? "text" : "password"} value={password} autoComplete="new-password" onChange={(event) => { setPassword(event.target.value); setError(""); }} placeholder="8-100 characters with a letter and number" className={`${inputClass} pr-12`} />
                    <button
                      type="button"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setShowPassword((visible) => !visible);
                      }}
                      aria-label={showPassword ? "Hide passwords" : "Show passwords"}
                      aria-pressed={showPassword}
                      title={showPassword ? "Hide passwords" : "Show passwords"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 transition hover:text-fuchsia-200"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="signup-confirm-password" className="bs-auth-field-label block">Confirm password <span className="text-red-400">*</span></label>
                  <input id="signup-confirm-password" type={showPassword ? "text" : "password"} value={confirmPassword} autoComplete="new-password" onChange={(event) => { setConfirmPassword(event.target.value); setError(""); }} placeholder="Repeat your password" className={inputClass} />
                </div>

                <div className="space-y-2">
                  <label htmlFor="signup-email" className="bs-auth-field-label block">Email <span className="text-xs font-normal text-gray-500">(optional, not used for recovery)</span></label>
                  <input id="signup-email" type="email" value={email} autoComplete="email" onChange={(event) => { setEmail(event.target.value); setError(""); }} placeholder="user@domain.tld" className={inputClass} />
                </div>

                <div className="space-y-2">
                  <label htmlFor="signup-phone" className="bs-auth-field-label block">Phone <span className="text-xs font-normal text-gray-500">(optional)</span></label>
                  <input id="signup-phone" type="tel" value={phone} inputMode="numeric" maxLength={10} onChange={(event) => setPhone(event.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="10-digit phone number" className={inputClass} />
                </div>

                <div className="space-y-2">
                  <label htmlFor="signup-dob" className="bs-auth-field-label block">Date of birth <span className="text-xs font-normal text-gray-500">(optional)</span></label>
                  <div className="relative">
                    <input id="signup-dob" value={dob} inputMode="numeric" onChange={handleDobChange} placeholder="DD/MM/YYYY" className={`${inputClass} pl-12 ${dobError ? "border-red-500" : ""}`} />
                    <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  </div>
                </div>
              </>
            ) : (
              <RecoveryPhraseSetup
                key={recoveryPhrase}
                phrase={recoveryPhrase}
                generating={phraseLoading}
                disabled={isLoading}
                onConfirmedChange={setRecoveryConfirmed}
                onRegenerate={() => void loadRecoveryPhrase()}
              />
            )}

            {error && <div className="rounded-md border border-red-500/20 bg-red-500/10 p-3"><p className="text-center text-sm text-red-300" role="alert">{error}</p></div>}
            {success && <div className="rounded-md border border-emerald-400/20 bg-emerald-400/10 p-3"><p className="text-center text-sm text-emerald-200">{success}</p></div>}

            <div className="flex flex-row gap-2">
              {stage === "recovery" && (
                <button
                  type="button"
                  disabled={isLoading}
                  onClick={() => { setStage("identity"); setError(""); }}
                  aria-label="Back to sign-up details"
                  title="Back to sign-up details"
                  className="bs-auth-back inline-flex items-center justify-center gap-2 transition disabled:opacity-50"
                >
                  <ArrowLeft size={18} /> Back
                </button>
              )}
              <button
                type="submit"
                disabled={isLoading || phraseLoading || (stage === "recovery" && !recoveryConfirmed)}
                className="bs-auth-primary flex min-h-12 flex-1 items-center justify-center gap-2 rounded-md bg-gradient-to-r from-fuchsia-200 via-cyan-200 to-amber-100 px-6 py-3.5 font-bold text-black transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading || phraseLoading ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-black border-t-transparent" /> : stage === "identity" ? <ShieldCheck size={18} /> : <UserPlus size={18} />}
                {phraseLoading ? "Generating..." : isLoading ? "Creating identity..." : stage === "identity" ? "Sign up" : "Create identity"}
              </button>
            </div>

            <div className="text-center text-sm text-gray-400">
              Already have an account?{" "}
              <button type="button" className="font-semibold text-fuchsia-200 transition hover:text-white" onClick={onSwitchToLogin}>Sign in</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SignupModal;
